import React from "react";

export interface PetSelectionItem {
    id: string | number;

    image?: string;
    photoUrl?: string;
    petImage?: string;

    label?: string;
    name?: string;
    petName?: string;

    petType?: string | number;

    // Pet size
    breedSize?: string;

    // Optional pet details
    breed?: string;
    gender?: string | number;
    weight?: number;
}

interface SelectPetsSectionProps {
    title: string;
    subtitle: string;
    data: PetSelectionItem[];
    selectedId?: string | number;
    onSelect: (item: PetSelectionItem) => void;
    imageSize?: string;
    showAddButton?: boolean;
    onAddClick?: () => void;
    className?: string;
    showTitle?: boolean;
    imageclassName?: string;
    textContainer?: string;
}

// Fallback Cartoon Avatars
const DEFAULT_DOG_AVATAR =
    "https://api.dicebear.com/7.x/bottts/svg?seed=Doggy1";

const DEFAULT_CAT_AVATAR =
    "https://api.dicebear.com/7.x/bottts/svg?seed=Kitty1";

const SelectPetsSection: React.FC<SelectPetsSectionProps> = ({
    title,
    subtitle,
    data = [],
    selectedId,
    onSelect,
    imageSize = "w-40 h-40",
    showAddButton = false,
    onAddClick,
    className = "",
    showTitle = true,
    imageclassName = "",
    textContainer = "",
}) => {
    return (
        <div className="flex flex-col h-full w-full items-center justify-center text-center">

            {/* TITLE */}
            {showTitle && (
                <div
                    className={`pb-6 w-full flex flex-col items-center justify-center ${textContainer}`}
                >
                    <h3 className="text-2xl font-semibold text-iconColor">
                        {title}
                    </h3>

                    <p className="text-xs text-gray-500 mt-0.5 max-w-md mx-auto">
                        {subtitle}
                    </p>
                </div>
            )}

            {/* PET LIST */}
            <div
                className={`flex flex-wrap items-center justify-center gap-5 lg:gap-20 flex-1 w-full ${className}`}
            >
                {data.map((item) => {
                    const isSelected =
                        String(selectedId) === String(item.id);

                    const displayName =
                        item.petName ||
                        item.name ||
                        item.label ||
                        "Pet";

                    /*
                     * Determine fallback avatar based on pet type.
                     *
                     * Backend:
                     * 1 = Dog
                     * 2 = Cat
                     */
                    const isCat =
                        Number(item.petType) === 2 ||
                        String(item.petType)
                            .trim()
                            .toLowerCase() === "cat";

                    const fallbackImage = isCat
                        ? DEFAULT_CAT_AVATAR
                        : DEFAULT_DOG_AVATAR;

                    const displayImage =
                        item.photoUrl ||
                        item.image ||
                        item.petImage ||
                        fallbackImage;

                    return (
                        <div
                            key={item.id}
                            className="flex flex-col items-center space-y-2"
                        >
                            {/* PET IMAGE */}
                            <button
                                type="button"
                                onClick={() => onSelect(item)}
                                className={`
                                    relative
                                    ${imageSize}
                                    rounded-full
                                    border-2
                                    transition-all
                                    duration-300
                                    hover:scale-105
                                    ${
                                        isSelected
                                            ? "border-mainBrandColor ring-4 ring-mainBrandColor/10"
                                            : "border-gray-200 hover:border-gray-400"
                                    }
                                `}
                            >
                                <div
                                    className={`
                                        w-full
                                        h-full
                                        rounded-full
                                        overflow-hidden
                                        bg-gray-50
                                        flex
                                        items-center
                                        justify-center
                                        ${imageclassName}
                                    `}
                                >
                                    <img
                                        src={displayImage}
                                        alt={displayName}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            (
                                                e.target as HTMLImageElement
                                            ).src = fallbackImage;
                                        }}
                                    />
                                </div>

                                {/* SELECTED CHECK */}
                                {isSelected && (
                                    <div
                                        className="
                                            absolute
                                            bottom-1
                                            right-1
                                            w-6
                                            h-6
                                            bg-mainBrandColor
                                            rounded-full
                                            flex
                                            items-center
                                            justify-center
                                            shadow-md
                                        "
                                    >
                                        <svg
                                            className="w-3.5 h-3.5 text-white"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth="3.5"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                    </div>
                                )}
                            </button>

                            {/* PET NAME */}
                            <p
                                className={`
                                    text-sm
                                    font-semibold
                                    ${
                                        isSelected
                                            ? "text-mainBrandColor"
                                            : "text-gray-600"
                                    }
                                `}
                            >
                                {displayName}
                            </p>

                            {/* PET SIZE */}
                            {item.breedSize && (
                                <p className="text-[11px] text-gray-400 capitalize">
                                    {item.breedSize}
                                </p>
                            )}
                        </div>
                    );
                })}

                {/* ADD PET */}
                {showAddButton && (
                    <div className="flex flex-col items-center space-y-2">

                        <button
                            type="button"
                            onClick={onAddClick}
                            className={`
                                ${imageSize}
                                rounded-full
                                border-2
                                border-dashed
                                border-gray-300
                                bg-white
                                hover:border-mainBrandColor
                                hover:bg-gray-50
                                transition-all
                                flex
                                items-center
                                justify-center
                                group
                                cursor-pointer
                            `}
                        >
                            <span
                                className="
                                    text-3xl
                                    font-light
                                    text-gray-400
                                    group-hover:text-mainBrandColor
                                "
                            >
                                +
                            </span>
                        </button>

                        <p className="text-sm font-medium text-gray-500">
                            Add Pet
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SelectPetsSection;